import { useState, useCallback } from 'react';
import { RefundsApi } from '@/api/clients/refunds/refunds.api';

export const useDownloadECheck = () => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);

  const downloadECheck = useCallback(async (refundId, comment = '') => {
    setDownloadLoading(true);
    setError(null);

    try {
      const response = await RefundsApi.downloadECheck(
        refundId,
        'download',
        comment,
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `echeck_${refundId}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Failed to download eCheck';
      setError(errorMessage);
      console.error('Error downloading eCheck:', err);
      return { success: false, error: errorMessage };
    } finally {
      setDownloadLoading(false);
    }
  }, []);

  const fetchECheckPdf = useCallback(async (refundId) => {
    setPreviewLoading(true);
    setError(null);

    try {
      console.log(`Fetching eCheck PDF for view: ${refundId}`);
      const response = await RefundsApi.downloadECheck(refundId, 'view');

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        setPdfPreview(e.target.result);
      };
      reader.readAsDataURL(blob);

      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Failed to load eCheck PDF';
      setError(errorMessage);
      console.error('Error fetching eCheck PDF:', err);
      return { success: false, error: errorMessage };
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const clearPdf = useCallback(() => {
    setPdfPreview(null);
    setError(null);
  }, []);

  const printPdf = useCallback(async (refundId, comment = '') => {
    setPrintLoading(true);
    try {
      const response = await RefundsApi.downloadECheck(
        refundId,
        'print',
        comment,
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      return { success: true };
    } catch (err) {
      console.error('Error opening eCheck PDF:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to open PDF',
      };
    } finally {
      setPrintLoading(false);
    }
  }, []);

  return {
    downloadECheck,
    fetchECheckPdf,
    clearPdf,
    printPdf,
    pdfPreview,
    downloadLoading,
    previewLoading,
    printLoading,
    error,
  };
};
